import { Box, TablePagination } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useState, useMemo } from "react";

import { fetchPoliciesWithFiltersAPI } from "api/siaApplication";
import { animateRouteProps } from "constants/common";
import { useT } from "translation";
import OutletHeader from "../Common/OutletHeader";
import PoliciesFilters from "./PoliciesFilters";
import PoliciesTable from "./PoliciesTable";
import { generatePoliciesData } from "./utils";
import { RootState } from "store/store";
import { useSelector } from "react-redux";
import { selectedAccountSelector } from "store/selectors/selectors";
import { userProfileDataSelector } from "store/selectors/userProfileSelectors";
import { ERole } from "modules/Accounts/types";

const defaultPoliciesTableData: PoliciesTableData = {
  policies: [],
  metaData: {
    total: -1,
    offset: -1,
    per_page: -1,
    total_page: -1,
  },
};

const defaultPoliciesFilter: FilterParams = {
  page: 1,
  per_page: 5,
  start_date: dayjs().subtract(30, "day").format("YYYY-MM-DD"),
  end_date: dayjs().format("YYYY-MM-DD"),
};

const Policies = () => {
  const { t } = useT();
  const [page, setPage] = useState(0);
  const [hasError, setHasError] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [initialCall, setIntialCall] = useState(true);
  const [loading, setLoading] = useState(false);
  const [policiesData, setPoliciesData] = useState<PoliciesTableData>(
    defaultPoliciesTableData
  );
  const [policiesFilters, setPoliciesFilters] = useState<FilterParams>(
    defaultPoliciesFilter
  );

  const selectedAccount = useSelector(selectedAccountSelector);
  const userProfile = useSelector(userProfileDataSelector);

  const isAdmin = useMemo(() => {
    const meta = userProfile?.meta?.roles;
    
    if (Array.isArray(meta)) {
      return meta.includes(ERole.ADMIN);
    }
    return false;
  }, [userProfile]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const onDateChange = (newValue: Dayjs, name: string) => {
    setPoliciesFilters((f) => ({
      ...f,
      [name]: newValue.format("YYYY-MM-DD"),
    }));
  };

  const onApply = (filterData: Filter[]) => {
    setPoliciesFilters(defaultPoliciesFilter);
  };

  const onClear = (filterData: Filter[]) => {
    setPoliciesFilters(defaultPoliciesFilter);
  };

  const fetchPolicies = async (payload: FilterParams) => {
    try {
      setLoading(true);
      const response = await fetchPoliciesWithFiltersAPI(payload);
      if (response?.status === 200 && response?.data?.data?.length) {
        setPoliciesData({
          policies: generatePoliciesData(response.data.data),
          metaData: response.data.meta,
        });
      } else {
        setPoliciesData(defaultPoliciesTableData);
      }
    } catch (error) {
      setHasError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Don't make API call until we have user profile data
    if (!userProfile) return;

    fetchPolicies({
      ...policiesFilters,
      page: page + 1,
      per_page: rowsPerPage,
      ...(isAdmin ? {} : { agent_id: selectedAccount?.member?.agent })
    });

    if (initialCall) setIntialCall(false);
  }, [rowsPerPage, page, policiesFilters, userProfile, isAdmin]);

  return (
    <Box
      sx={{
        backgroundColor: "white",
        borderRadius: "12px",
      }}
      {...animateRouteProps}
    >
      <OutletHeader
        title={t("POLICIES_PAGE.title")}
        subTitle={t("POLICIES_PAGE.desc")}
      />
      <PoliciesFilters
        onDateChange={onDateChange}
        onApply={onApply}
        onClear={onClear}
      />
      <Box sx={{ width: "100%", mt: "20px" }}>
        <Box sx={{ width: "100%", mb: 2 }}>
          <PoliciesTable
            policiesDataList={policiesData.policies}
            hasError={hasError}
            loadingData={loading}
          />
          <TablePagination
            sx={{ color: "black" }}
            labelRowsPerPage={t("TABLES.rowsPerPage")}
            labelDisplayedRows={({ from, to, count }) => {
              return `${from}–${to} ${t("TABLES.of")} ${
                count !== -1 ? count : `${t("TABLES.moreThan")} ${to}`
              }`;
            }}
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={policiesData?.metaData?.total ?? rowsPerPage}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default Policies;
